import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { api } from "../lib/api";

// Round 16 · Change password for signed-in users. Opened from the account
// menu (TopNav). Talks to POST /api/brand-os/auth/change-password and shows
// every error inline. Never logs or echoes a password.
const MIN_PASSWORD_LENGTH = 10;

export function describeChangePasswordError(code, detail) {
  switch (code) {
    case "wrong_current_password":
      return "That current password isn't right.";
    case "password_mismatch":
      return "The two new passwords don't match.";
    case "password_too_short":
      return `Choose a longer password — at least ${detail?.min_length || MIN_PASSWORD_LENGTH} characters.`;
    case "password_unchanged":
      return "Your new password must be different from the current one.";
    case "too_many_attempts": {
      const mins = Math.max(1, Math.ceil((detail?.retry_after_seconds || 60) / 60));
      return `Too many failed attempts. Try again in about ${mins} minute${mins === 1 ? "" : "s"}.`;
    }
    default:
      return "We couldn't change your password. Please try again.";
  }
}

export function ChangePasswordDialog({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const firstRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Reset + focus ONLY when the dialog opens. (Depending on `onClose` here
  // would re-run on every parent re-render — e.g. the bell poll — and wipe
  // what the user has typed.)
  useEffect(() => {
    if (!open) return undefined;
    setCurrent(""); setNext(""); setConfirm(""); setError(""); setSuccess(null); setBusy(false);
    const t = setTimeout(() => firstRef.current?.focus(), 30);
    const onKey = (e) => { if (e.key === "Escape") onCloseRef.current?.(); };
    window.addEventListener("keydown", onKey);
    return () => { clearTimeout(t); window.removeEventListener("keydown", onKey); };
  }, [open]);

  if (!open) return null;

  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && next !== confirm;
  const sameAsCurrent = next.length > 0 && current.length > 0 && next === current;
  const canSubmit = !busy && current && next.length >= MIN_PASSWORD_LENGTH && next === confirm && !sameAsCurrent;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
        confirm_password: confirm,
      });
      setSuccess(r.data || { ok: true });
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = detail && typeof detail === "object" ? detail.code : undefined;
      setError(describeChangePasswordError(code, detail));
    } finally {
      setBusy(false);
    }
  }

  // Portal: the top bar is a blurred/sticky container, so a fixed overlay
  // rendered inside it would be clipped/offset.
  return createPortal(
    <div
      className="bo-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bo-change-password-title"
      data-testid="change-password-dialog"
      onClick={(e) => { if (e.currentTarget === e.target) onClose?.(); }}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(26, 26, 46, 0.35)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1200, padding: 16, overflowY: "auto",
      }}
    >
      <div
        className="bo-card"
        style={{ width: "min(440px, 100%)", background: "var(--bo-panel)", boxShadow: "var(--bo-shadow-lg)", minWidth: 0 }}
      >
        <div className="bo-eyebrow">Account</div>
        <h2 id="bo-change-password-title" className="bo-serif" style={{ fontSize: 20, margin: "6px 0 14px" }}>
          Change password
        </h2>

        {success ? (
          <div data-testid="change-password-success">
            <div
              className="bo-flash"
              role="status"
              style={{ padding: "12px 14px", borderRadius: 10, background: "var(--bo-panel-alt)", fontSize: 14, lineHeight: 1.5 }}
            >
              <strong>Your password was changed.</strong>
              {" "}You stay signed in here
              {typeof success.other_sessions_revoked === "number" && success.other_sessions_revoked > 0
                ? `; ${success.other_sessions_revoked} other session${success.other_sessions_revoked === 1 ? " was" : "s were"} signed out.`
                : "."}
              {success.changed_at_eastern ? ` A confirmation e-mail is on its way (changed ${success.changed_at_eastern}).` : ""}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
              <button type="button" className="bo-btn bo-btn-primary" onClick={onClose} data-testid="change-password-done">
                Done
              </button>
            </div>
          </div>
        ) : (
          <form className="bo-login-form" onSubmit={handleSubmit} data-testid="change-password-form" autoComplete="off">
            <div className="bo-field">
              <label className="bo-label" htmlFor="bo-cp-current">Current password</label>
              <input
                id="bo-cp-current" ref={firstRef} className="bo-input" type="password"
                autoComplete="current-password" required value={current}
                onChange={(e) => setCurrent(e.target.value)} disabled={busy}
                data-testid="change-password-current"
              />
            </div>
            <div className="bo-field">
              <label className="bo-label" htmlFor="bo-cp-new">New password</label>
              <input
                id="bo-cp-new" className="bo-input" type="password"
                autoComplete="new-password" required minLength={MIN_PASSWORD_LENGTH} value={next}
                onChange={(e) => setNext(e.target.value)} disabled={busy}
                aria-invalid={tooShort || sameAsCurrent ? "true" : "false"}
                data-testid="change-password-new"
              />
              <div style={{ fontSize: 12, color: "var(--bo-muted)" }}>At least {MIN_PASSWORD_LENGTH} characters.</div>
            </div>
            <div className="bo-field">
              <label className="bo-label" htmlFor="bo-cp-confirm">Confirm new password</label>
              <input
                id="bo-cp-confirm" className="bo-input" type="password"
                autoComplete="new-password" required minLength={MIN_PASSWORD_LENGTH} value={confirm}
                onChange={(e) => setConfirm(e.target.value)} disabled={busy}
                aria-invalid={mismatch ? "true" : "false"}
                data-testid="change-password-confirm"
              />
            </div>

            {tooShort && (
              <div className="bo-login-error" role="alert" data-testid="change-password-too-short">
                Choose at least {MIN_PASSWORD_LENGTH} characters.
              </div>
            )}
            {!tooShort && sameAsCurrent && (
              <div className="bo-login-error" role="alert" data-testid="change-password-unchanged">
                Your new password must be different from the current one.
              </div>
            )}
            {!tooShort && !sameAsCurrent && mismatch && (
              <div className="bo-login-error" role="alert" data-testid="change-password-mismatch">
                The two new passwords don&rsquo;t match.
              </div>
            )}
            {error && (
              <div className="bo-login-error" role="alert" data-testid="change-password-error">
                {error}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button type="button" className="bo-btn bo-btn-ghost" onClick={onClose} disabled={busy} data-testid="change-password-cancel">
                Cancel
              </button>
              <button type="submit" className="bo-btn bo-btn-primary" disabled={!canSubmit} data-testid="change-password-submit">
                {busy ? "Changing…" : "Change password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}
